import { useState, useEffect, useCallback } from "react";
import { Routes, Route, useParams, useNavigate, useLocation } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Mail, 
  Copy, 
  RefreshCw, 
  Plus, 
  Trash2, 
  Inbox, 
  Clock, 
  CheckCheck, 
  ArrowLeft,
  MailOpen,
  Sparkles,
  History,
  Settings,
  Lock,
  Eye,
  EyeOff,
  Save,
  X,
  Shield,
  Activity,
  Link,
  ExternalLink,
  Paperclip,
  FileText,
  Image,
  File,
  Download
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Admin Panel Component
const AdminPanel = ({ onClose, adminCode, onEmailDeleted }) => {
  const [newCode, setNewCode] = useState("");
  const [showNewCode, setShowNewCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allEmails, setAllEmails] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);

  useEffect(() => {
    loadAllEmails();
  }, []);

  const loadAllEmails = async () => {
    try {
      setLoadingEmails(true);
      const response = await axios.get(`${API}/admin/emails?code=${adminCode}`);
      setAllEmails(response.data);
    } catch (error) {
      console.error("Error loading emails:", error);
    } finally {
      setLoadingEmails(false);
    }
  };

  const updateAdminCode = async () => {
    if (!newCode || newCode.length < 4) {
      toast.error("Kode minimal 4 karakter");
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/admin/update-code?code=${adminCode}`, { new_code: newCode });
      toast.success("Kode admin berhasil diubah");
      setNewCode("");
    } catch (error) {
      toast.error("Gagal mengubah kode admin");
    } finally {
      setLoading(false);
    }
  };

  const deleteEmail = async (emailId) => {
    try {
      await axios.delete(`${API}/admin/emails/${emailId}?code=${adminCode}`);
      setAllEmails(prev => prev.filter(e => e.id !== emailId));
      toast.success("Email berhasil dihapus");
      if (onEmailDeleted) onEmailDeleted();
    } catch (error) {
      toast.error("Gagal menghapus email");
    }
  };

  const deleteAllEmails = async () => {
    try {
      await axios.delete(`${API}/admin/emails?code=${adminCode}`);
      setAllEmails([]);
      toast.success("Semua email berhasil dihapus");
      if (onEmailDeleted) onEmailDeleted();
    } catch (error) {
      toast.error("Gagal menghapus semua email");
    }
  };

  const getRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return "Baru saja";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m lalu`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}j lalu`;
    return `${Math.floor(diff / 86400000)}h lalu`;
  };

  const uniqueIPs = new Set(allEmails.filter(e => e.client_ip && e.client_ip !== "N/A").map(e => e.client_ip)).size;
  const todayEmails = allEmails.filter(e => {
    const date = new Date(e.created_at);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }).length;

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-700 p-6 text-white">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-32 translate-x-32" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-24 -translate-x-24" />
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
              <Shield className="w-7 h-7 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="font-heading text-2xl font-bold">Admin Panel</h2>
              <p className="text-indigo-200 text-sm">Kelola TempMail Anda</p>
            </div>
          </div>
          <Button variant="ghost" onClick={onClose} className="rounded-full text-white hover:bg-white/20 h-10 w-10 p-0">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-4 border border-blue-200/50">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-blue-500 flex items-center justify-center shadow-lg shadow-blue-200">
              <Mail className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-700">{allEmails.length}</p>
              <p className="text-xs text-blue-600 font-medium">Total Email</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-2xl p-4 border border-emerald-200/50">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-200">
              <Activity className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-700">{todayEmails}</p>
              <p className="text-xs text-emerald-600 font-medium">Hari Ini</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-violet-50 to-violet-100/50 rounded-2xl p-4 border border-violet-200/50">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-violet-500 flex items-center justify-center shadow-lg shadow-violet-200">
              <History className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-2xl font-bold text-violet-700">{uniqueIPs}</p>
              <p className="text-xs text-violet-600 font-medium">Unique IP</p>
            </div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="dashboard" className="w-full">
        <TabsList className="grid w-full grid-cols-3 bg-slate-100/80 p-1.5 rounded-xl h-12">
          <TabsTrigger value="dashboard" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-medium">
            <Inbox className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="emails" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-medium">
            <Mail className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Email
          </TabsTrigger>
          <TabsTrigger value="settings" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-medium">
            <Settings className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Pengaturan
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-6 space-y-4">
          <Card className="border-slate-200/80 shadow-sm bg-white/80">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shadow-lg shadow-green-200">
                    <CheckCheck className="w-6 h-6 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Email API</h3>
                    <p className="text-sm text-slate-500">Terhubung & Aktif</p>
                  </div>
                </div>
                <Badge className="bg-green-100 text-green-700 hover:bg-green-100 px-3 py-1">
                  <span className="w-2 h-2 rounded-full bg-green-500 mr-2 animate-pulse" />
                  Online
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200/80 shadow-sm bg-white/80">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
                Aktivitas Terbaru
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100">
                {allEmails.slice(0, 5).map((email, index) => (
                  <div key={email.id} className="px-5 py-3.5 flex items-center justify-between hover:bg-slate-50/50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${index === 0 ? 'bg-indigo-100' : 'bg-slate-100'}`}>
                        <Mail className={`w-4 h-4 ${index === 0 ? 'text-indigo-600' : 'text-slate-400'}`} strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="font-mono text-sm text-slate-700">{email.email_address}</p>
                        <p className="text-xs text-slate-400">{email.client_ip || "N/A"}</p>
                      </div>
                    </div>
                    <Badge variant="secondary" className="text-xs font-normal">{getRelativeTime(email.created_at)}</Badge>
                  </div>
                ))}
                {allEmails.length === 0 && (
                  <div className="px-5 py-10 text-center text-slate-400">
                    <MailOpen className="w-10 h-10 mx-auto mb-3 text-slate-300" strokeWidth={1.5} />
                    <p>Belum ada aktivitas</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="emails" className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <Badge variant="secondary" className="rounded-full px-3 py-1">{allEmails.length} email</Badge>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={loadAllEmails} disabled={loadingEmails} className="rounded-full">
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingEmails ? 'animate-spin' : ''}`} strokeWidth={1.5} />
                Refresh
              </Button>
              {allEmails.length > 0 && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" size="sm" className="rounded-full">
                      <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      Hapus Semua
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Hapus Semua Email?</AlertDialogTitle>
                      <AlertDialogDescription>Semua {allEmails.length} email akan dihapus permanen.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-full">Batal</AlertDialogCancel>
                      <AlertDialogAction className="rounded-full bg-red-500 hover:bg-red-600" onClick={deleteAllEmails}>Hapus Semua</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>

          {loadingEmails ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 text-slate-300 animate-spin mx-auto mb-3" />
              <p className="text-slate-400">Memuat data...</p>
            </div>
          ) : allEmails.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <MailOpen className="w-8 h-8 text-slate-300" strokeWidth={1.5} />
              </div>
              <p className="text-slate-500 font-medium">Belum ada email</p>
            </div>
          ) : (
            <Card className="border-slate-200/80 shadow-sm overflow-hidden bg-white/80">
              <ScrollArea className="h-[320px]">
                <Table>
                  <TableHeader className="bg-slate-50/80 sticky top-0">
                    <TableRow>
                      <TableHead className="text-xs font-semibold uppercase text-slate-500">Email</TableHead>
                      <TableHead className="text-xs font-semibold uppercase text-slate-500">IP</TableHead>
                      <TableHead className="text-xs font-semibold uppercase text-slate-500">Dibuat</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allEmails.map((email) => (
                      <TableRow key={email.id} className="hover:bg-slate-50/50">
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                              <Mail className="w-4 h-4 text-indigo-600" strokeWidth={1.5} />
                            </div>
                            <span className="font-mono text-sm">{email.email_address}</span>
                          </div>
                        </TableCell>
                        <TableCell><code className="text-xs bg-slate-100 px-2 py-1 rounded">{email.client_ip || "N/A"}</code></TableCell>
                        <TableCell className="text-sm text-slate-500">{getRelativeTime(email.created_at)}</TableCell>
                        <TableCell>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg">
                                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Hapus Email?</AlertDialogTitle>
                                <AlertDialogDescription>Email <code>{email.email_address}</code> akan dihapus permanen.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="rounded-full">Batal</AlertDialogCancel>
                                <AlertDialogAction className="rounded-full bg-red-500 hover:bg-red-600" onClick={() => deleteEmail(email.id)}>Hapus</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="settings" className="mt-6 space-y-4">
          <Card className="border-slate-200/80 shadow-sm bg-white/80">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Lock className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
                Ubah Kode Admin
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-code" className="text-slate-600">Kode Admin Baru</Label>
                <div className="relative">
                  <Input id="new-code" type={showNewCode ? "text" : "password"} placeholder="Masukkan kode baru..." value={newCode} onChange={(e) => setNewCode(e.target.value)} className="pr-10 h-12 rounded-xl border-slate-200" />
                  <Button type="button" variant="ghost" size="icon" className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8" onClick={() => setShowNewCode(!showNewCode)}>
                    {showNewCode ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-slate-400" />}
                  </Button>
                </div>
              </div>
              <Button onClick={updateAdminCode} disabled={loading || !newCode} className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700 h-11">
                <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
                {loading ? "Menyimpan..." : "Simpan Kode Baru"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Email View Component (for direct link access)
const EmailView = () => {
  const { emailAddress } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState(null);
  const [inbox, setInbox] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadEmailByAddress();
  }, [emailAddress]);

  useEffect(() => {
    if (email) {
      loadInbox(email.id);
    }
  }, [email]);

  const loadEmailByAddress = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API}/emails/address/${emailAddress}`);
      setEmail(response.data);
    } catch (error) {
      console.error("Error loading email:", error);
      setError("Email tidak ditemukan");
    } finally {
      setLoading(false);
    }
  };

  const loadInbox = async (emailId) => {
    try {
      setRefreshing(true);
      const response = await axios.get(`${API}/emails/${emailId}/inbox`);
      setInbox(response.data);
    } catch (error) {
      console.error("Error loading inbox:", error);
    } finally {
      setRefreshing(false);
    }
  };

  const copyEmail = async () => {
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email.email_address);
      setCopied(true);
      toast.success("Email berhasil disalin!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Gagal menyalin email");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link berhasil disalin!");
    } catch (error) {
      toast.error("Gagal menyalin link");
    }
  };

  const refreshInbox = () => {
    if (email) {
      loadInbox(email.id);
      toast.info("Inbox diperbarui");
    }
  };

  const viewMessage = async (message) => {
    try {
      const response = await axios.get(`${API}/inbox/${message.id}?email_id=${email.id}`);
      setSelectedMessage(response.data);
      setInbox(prev => prev.map(m => m.id === message.id ? { ...m, is_read: true } : m));
    } catch (error) {
      toast.error("Gagal memuat pesan");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return "Baru saja";
    if (diff < 3600000) return `${Math.floor(diff / 60000)} menit lalu`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} jam lalu`;
    return date.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const unreadCount = inbox.filter(m => !m.is_read).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-10 h-10 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Memuat email...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-4">
            <MailOpen className="w-10 h-10 text-red-400" strokeWidth={1.5} />
          </div>
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">Email Tidak Ditemukan</h2>
          <p className="text-slate-500 mb-6">Email {emailAddress} tidak ditemukan dalam sistem.</p>
          <Button onClick={() => navigate('/')} className="rounded-full bg-indigo-600 hover:bg-indigo-700">
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Kembali ke Beranda
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="bottom-right" richColors />
      
      {/* Header */}
      <header className="bg-white/70 backdrop-blur-lg sticky top-0 z-50 border-b border-slate-200/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/')} className="rounded-full hover:bg-slate-100 mr-2">
                <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
              </Button>
              <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-200">
                <Mail className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="font-heading text-lg font-bold text-slate-900 tracking-tight">TempMail</h1>
                <p className="text-xs text-slate-500">Akses Langsung</p>
              </div>
            </div>
            <Button onClick={copyLink} variant="outline" className="rounded-full">
              <Link className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Salin Link
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Email Card */}
        <Card className="shadow-soft border-slate-200 mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-200">
                  <Mail className="w-7 h-7 text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Email Aktif</p>
                  <p className="font-mono text-xl font-semibold text-slate-900">{email.email_address}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={copyEmail} variant="outline" className={`rounded-full ${copied ? 'bg-green-50 border-green-200 text-green-600' : ''}`}>
                  {copied ? <><CheckCheck className="w-4 h-4 mr-2" strokeWidth={1.5} />Tersalin!</> : <><Copy className="w-4 h-4 mr-2" strokeWidth={1.5} />Salin Email</>}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Inbox */}
        {selectedMessage ? (
          <Card className="shadow-soft border-slate-200">
            <CardHeader className="border-b border-slate-100">
              <div className="flex items-center justify-between">
                <Button variant="ghost" onClick={() => setSelectedMessage(null)} className="rounded-full hover:bg-slate-100">
                  <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Kembali
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="mb-6">
                <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">{selectedMessage.subject}</h2>
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                      <Mail className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
                    </div>
                    <span className="font-medium text-slate-700">{selectedMessage.sender}</span>
                  </div>
                  <Separator orientation="vertical" className="h-4" />
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {formatDate(selectedMessage.received_at)}
                  </span>
                </div>
              </div>
              <Separator className="my-6" />
              
              {/* Attachments */}
              {selectedMessage.attachments && selectedMessage.attachments.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Paperclip className="w-4 h-4" strokeWidth={1.5} />
                    Lampiran ({selectedMessage.attachments.length})
                  </h4>
                  <div className="grid gap-2">
                    {selectedMessage.attachments.map((att) => {
                      const getFileIcon = (contentType) => {
                        if (contentType.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
                        if (contentType.includes('image')) return <Image className="w-5 h-5 text-blue-500" />;
                        return <File className="w-5 h-5 text-slate-500" />;
                      };
                      const formatSize = (bytes) => {
                        if (bytes < 1024) return `${bytes} B`;
                        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
                        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
                      };
                      return (
                        <a
                          key={att.id}
                          href={`${API}/attachment/${email.id}/${selectedMessage.id}/${att.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 rounded-xl border border-slate-200 transition-colors group"
                        >
                          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center border border-slate-200">
                            {getFileIcon(att.content_type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-700 truncate">{att.filename}</p>
                            <p className="text-xs text-slate-400">{formatSize(att.size)}</p>
                          </div>
                          <Download className="w-5 h-5 text-slate-400 group-hover:text-indigo-600" strokeWidth={1.5} />
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
              
              <div className="prose prose-slate max-w-none">
                {selectedMessage.body.includes('<') ? (
                  <div dangerouslySetInnerHTML={{ __html: selectedMessage.body }} />
                ) : (
                  <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{selectedMessage.body}</p>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="shadow-soft border-slate-200">
            <CardHeader className="border-b border-slate-100">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-slate-500 uppercase tracking-wider flex items-center gap-2">
                  <Inbox className="w-4 h-4" strokeWidth={1.5} />
                  Inbox
                  {unreadCount > 0 && <Badge className="bg-indigo-600 text-white rounded-full px-2 py-0.5 text-xs">{unreadCount}</Badge>}
                </CardTitle>
                <Button variant="ghost" onClick={refreshInbox} disabled={refreshing} className="rounded-full hover:bg-slate-100">
                  <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} strokeWidth={1.5} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {inbox.length === 0 ? (
                <div className="py-16 text-center">
                  <div className="w-20 h-20 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                    <MailOpen className="w-10 h-10 text-slate-300" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-slate-700 mb-2">Inbox Kosong</h3>
                  <p className="text-slate-500 text-sm max-w-xs mx-auto">Belum ada email masuk untuk alamat ini.</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="divide-y divide-slate-100">
                    {inbox.map((message) => (
                      <div
                        key={message.id}
                        className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${!message.is_read ? 'bg-indigo-50/50' : ''}`}
                        onClick={() => viewMessage(message)}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {!message.is_read && <div className="w-2 h-2 rounded-full bg-indigo-600" />}
                              <p className={`text-sm truncate ${!message.is_read ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>{message.sender}</p>
                              {message.attachments && message.attachments.length > 0 && (
                                <span className="flex items-center gap-1 text-xs text-slate-400">
                                  <Paperclip className="w-3 h-3" strokeWidth={1.5} />
                                  {message.attachments.length}
                                </span>
                              )}
                            </div>
                            <p className={`text-sm truncate mb-1 ${!message.is_read ? 'font-medium text-slate-800' : 'text-slate-600'}`}>{message.subject}</p>
                            <p className="text-xs text-slate-400 truncate">{message.body.replace(/<[^>]*>/g, '').substring(0, 100)}...</p>
                          </div>
                          <span className="text-xs text-slate-400 whitespace-nowrap">{formatDate(message.received_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

// Main App Component
function MainApp() {
  const navigate = useNavigate();
  const [currentEmail, setCurrentEmail] = useState(null);
  const [emailHistory, setEmailHistory] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminCode, setAdminCode] = useState("");
  const [adminCodeInput, setAdminCodeInput] = useState("");
  const [showCodeInput, setShowCodeInput] = useState(false);

  useEffect(() => {
    loadEmailHistory();
  }, []);

  useEffect(() => {
    if (currentEmail) {
      loadInbox(currentEmail.id);
    }
  }, [currentEmail]);

  const loadEmailHistory = async () => {
    try {
      const response = await axios.get(`${API}/emails`);
      setEmailHistory(response.data);
      if (response.data.length > 0 && !currentEmail) {
        setCurrentEmail(response.data[0]);
      }
    } catch (error) {
      console.error("Error loading email history:", error);
    }
  };

  const loadInbox = useCallback(async (emailId) => {
    try {
      setRefreshing(true);
      const response = await axios.get(`${API}/emails/${emailId}/inbox`);
      setInbox(response.data);
    } catch (error) {
      console.error("Error loading inbox:", error);
    } finally {
      setRefreshing(false);
    }
  }, []);

  const generateNewEmail = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/emails/generate`);
      setCurrentEmail(response.data);
      setInbox([]);
      setSelectedMessage(null);
      await loadEmailHistory();
      toast.success("Email baru berhasil dibuat!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal membuat email baru");
    } finally {
      setLoading(false);
    }
  };

  const copyEmail = async () => {
    if (!currentEmail) return;
    try {
      await navigator.clipboard.writeText(currentEmail.email_address);
      setCopied(true);
      toast.success("Email berhasil disalin!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Gagal menyalin email");
    }
  };

  const copyLink = async () => {
    if (!currentEmail) return;
    try {
      const link = `${window.location.origin}/${currentEmail.email_address}`;
      await navigator.clipboard.writeText(link);
      setLinkCopied(true);
      toast.success("Link berhasil disalin!");
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (error) {
      toast.error("Gagal menyalin link");
    }
  };

  const openInNewTab = () => {
    if (!currentEmail) return;
    const link = `${window.location.origin}/${currentEmail.email_address}`;
    window.open(link, '_blank');
  };

  const refreshInbox = () => {
    if (currentEmail) {
      loadInbox(currentEmail.id);
      toast.info("Inbox diperbarui");
    }
  };

  const selectEmail = (email) => {
    setCurrentEmail(email);
    setSelectedMessage(null);
  };

  const viewMessage = async (message) => {
    try {
      const response = await axios.get(`${API}/inbox/${message.id}?email_id=${currentEmail.id}`);
      setSelectedMessage(response.data);
      setInbox(prev => prev.map(m => m.id === message.id ? { ...m, is_read: true } : m));
    } catch (error) {
      toast.error("Gagal memuat pesan");
    }
  };

  const deleteMessage = async (messageId) => {
    try {
      await axios.delete(`${API}/inbox/${messageId}?email_id=${currentEmail?.id}`);
      setInbox(prev => prev.filter(m => m.id !== messageId));
      if (selectedMessage?.id === messageId) setSelectedMessage(null);
      toast.success("Pesan berhasil dihapus");
    } catch (error) {
      toast.error("Gagal menghapus pesan");
    }
  };

  const deleteEmail = async (emailId) => {
    try {
      await axios.delete(`${API}/emails/${emailId}`);
      setEmailHistory(prev => prev.filter(e => e.id !== emailId));
      if (currentEmail?.id === emailId) {
        const remaining = emailHistory.filter(e => e.id !== emailId);
        setCurrentEmail(remaining.length > 0 ? remaining[0] : null);
        setInbox([]);
        setSelectedMessage(null);
      }
      toast.success("Email berhasil dihapus");
    } catch (error) {
      toast.error("Gagal menghapus email");
    }
  };

  const handleAdminLogin = async () => {
    try {
      await axios.post(`${API}/admin/login`, { code: adminCodeInput });
      setAdminCode(adminCodeInput);
      setShowAdminLogin(false);
      setShowAdminPanel(true);
      toast.success("Login admin berhasil!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kode admin salah");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return "Baru saja";
    if (diff < 3600000) return `${Math.floor(diff / 60000)} menit lalu`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} jam lalu`;
    return date.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const unreadCount = inbox.filter(m => !m.is_read).length;

  return (
    <div className="app-container min-h-screen" data-testid="temp-mail-app">
      <Toaster position="bottom-right" richColors />
      
      {/* Admin Login Dialog */}
      <Dialog open={showAdminLogin} onOpenChange={setShowAdminLogin}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-indigo-600" strokeWidth={1.5} />
              Login Admin
            </DialogTitle>
            <DialogDescription>Masukkan kode admin untuk mengakses panel pengaturan.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="admin-code">Kode Admin</Label>
              <div className="relative">
                <Input id="admin-code" type={showCodeInput ? "text" : "password"} placeholder="Masukkan kode..." value={adminCodeInput} onChange={(e) => setAdminCodeInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleAdminLogin()} className="pr-10 h-12 rounded-lg" data-testid="admin-code-input" />
                <Button type="button" variant="ghost" size="icon" className="absolute right-2 top-1/2 -translate-y-1/2" onClick={() => setShowCodeInput(!showCodeInput)}>
                  {showCodeInput ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdminLogin(false)} className="rounded-full">Batal</Button>
            <Button onClick={handleAdminLogin} className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="admin-login-btn">
              <Lock className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Masuk
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Panel Dialog */}
      <Dialog open={showAdminPanel} onOpenChange={setShowAdminPanel}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto p-0">
          <DialogHeader className="sr-only">
            <DialogTitle>Admin Panel</DialogTitle>
            <DialogDescription>Panel administrasi untuk mengelola email.</DialogDescription>
          </DialogHeader>
          <div className="p-6">
            <AdminPanel onClose={() => setShowAdminPanel(false)} adminCode={adminCode} onEmailDeleted={() => { loadEmailHistory(); setCurrentEmail(null); setInbox([]); setSelectedMessage(null); }} />
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Header */}
      <header className="header-glass sticky top-0 z-50 border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-200">
                <Mail className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="font-heading text-xl font-bold text-slate-900 tracking-tight">TempMail</h1>
                <p className="text-xs text-slate-500">Email sementara yang aman</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => setShowAdminLogin(true)} className="rounded-full hover:bg-slate-100" data-testid="admin-btn">
                <Settings className="w-4 h-4" strokeWidth={1.5} />
              </Button>
              <Button onClick={generateNewEmail} disabled={loading} className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white px-6 shadow-lg shadow-indigo-200 transition-all hover:-translate-y-0.5" data-testid="generate-email-btn">
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                {loading ? "Membuat..." : "Email Baru"}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!currentEmail ? (
          <div className="flex flex-col items-center justify-center py-20 animate-fade-in-up">
            <div className="w-24 h-24 rounded-2xl bg-slate-100 flex items-center justify-center mb-6">
              <Sparkles className="w-12 h-12 text-slate-400" strokeWidth={1.5} />
            </div>
            <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">Selamat Datang!</h2>
            <p className="text-slate-500 mb-8 text-center max-w-md">Buat email sementara untuk melindungi privasi Anda. Email dapat menerima pesan sungguhan.</p>
            <Button onClick={generateNewEmail} disabled={loading} className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-lg shadow-xl shadow-indigo-200 transition-all hover:-translate-y-1" data-testid="generate-first-email-btn">
              <Sparkles className="w-5 h-5 mr-2" strokeWidth={1.5} />
              {loading ? "Membuat Email..." : "Buat Email Pertama"}
            </Button>
          </div>
        ) : (
          <div className="bento-grid">
            {/* Left Sidebar */}
            <div className="space-y-6">
              <Card className="shadow-soft border-slate-200 animate-fade-in-up" data-testid="current-email-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-slate-500 uppercase tracking-wider flex items-center gap-2">
                    <Mail className="w-4 h-4" strokeWidth={1.5} />
                    Email Aktif
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 mb-4 cursor-pointer group card-hover" onClick={copyEmail} data-testid="email-address-display">
                    <p className="font-mono text-lg text-slate-900 break-all group-hover:text-indigo-600 transition-colors">{currentEmail.email_address}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <Button onClick={copyEmail} variant="outline" className={`rounded-full border-slate-200 hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-600 transition-all ${copied ? 'bg-green-50 border-green-200 text-green-600' : ''}`} data-testid="copy-email-btn">
                      {copied ? <><CheckCheck className="w-4 h-4 mr-1" strokeWidth={1.5} />Tersalin</> : <><Copy className="w-4 h-4 mr-1" strokeWidth={1.5} />Salin</>}
                    </Button>
                    <Button onClick={copyLink} variant="outline" className={`rounded-full border-slate-200 hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-600 transition-all ${linkCopied ? 'bg-green-50 border-green-200 text-green-600' : ''}`}>
                      {linkCopied ? <><CheckCheck className="w-4 h-4 mr-1" strokeWidth={1.5} />Tersalin</> : <><Link className="w-4 h-4 mr-1" strokeWidth={1.5} />Link</>}
                    </Button>
                  </div>
                  <Button onClick={openInNewTab} variant="ghost" className="w-full rounded-full text-slate-500 hover:text-indigo-600 hover:bg-indigo-50">
                    <ExternalLink className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Buka di Tab Baru
                  </Button>
                </CardContent>
              </Card>

              <Card className="shadow-soft border-slate-200 animate-fade-in-up stagger-1" data-testid="email-history-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-slate-500 uppercase tracking-wider flex items-center gap-2">
                    <History className="w-4 h-4" strokeWidth={1.5} />
                    Riwayat Email ({emailHistory.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[280px]">
                    <div className="px-4 pb-4">
                      {emailHistory.map((email) => (
                        <div key={email.id} className={`p-3 rounded-xl cursor-pointer transition-all mb-2 ${currentEmail?.id === email.id ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-slate-50 border border-transparent'}`} onClick={() => selectEmail(email)}>
                          <div className="flex items-center justify-between">
                            <p className="font-mono text-sm text-slate-700 truncate flex-1 mr-2">{email.email_address}</p>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-500 hover:bg-red-50" onClick={(e) => e.stopPropagation()}>
                                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Hapus Email?</AlertDialogTitle>
                                  <AlertDialogDescription>Email dan semua pesan di inbox akan dihapus permanen.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="rounded-full">Batal</AlertDialogCancel>
                                  <AlertDialogAction className="rounded-full bg-red-500 hover:bg-red-600" onClick={() => deleteEmail(email.id)}>Hapus</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                          <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                            <Clock className="w-3 h-3" strokeWidth={1.5} />
                            {formatDate(email.created_at)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>

            {/* Right Content */}
            <div className="space-y-6">
              {selectedMessage ? (
                <Card className="shadow-soft border-slate-200 animate-fade-in-up" data-testid="message-detail-card">
                  <CardHeader className="border-b border-slate-100">
                    <div className="flex items-center justify-between">
                      <Button variant="ghost" onClick={() => setSelectedMessage(null)} className="rounded-full hover:bg-slate-100" data-testid="back-to-inbox-btn">
                        <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        Kembali
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" className="rounded-full text-red-500 hover:bg-red-50 hover:text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                            Hapus
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Hapus Pesan?</AlertDialogTitle>
                            <AlertDialogDescription>Pesan ini akan dihapus permanen.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-full">Batal</AlertDialogCancel>
                            <AlertDialogAction className="rounded-full bg-red-500 hover:bg-red-600" onClick={() => deleteMessage(selectedMessage.id)}>Hapus</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="mb-6">
                      <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">{selectedMessage.subject}</h2>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                            <Mail className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
                          </div>
                          <span className="font-medium text-slate-700">{selectedMessage.sender}</span>
                        </div>
                        <Separator orientation="vertical" className="h-4" />
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
                          {formatDate(selectedMessage.received_at)}
                        </span>
                      </div>
                    </div>
                    <Separator className="my-6" />
                    
                    {/* Attachments */}
                    {selectedMessage.attachments && selectedMessage.attachments.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                          <Paperclip className="w-4 h-4" strokeWidth={1.5} />
                          Lampiran ({selectedMessage.attachments.length})
                        </h4>
                        <div className="grid gap-2">
                          {selectedMessage.attachments.map((att) => {
                            const getFileIcon = (contentType) => {
                              if (contentType.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
                              if (contentType.includes('image')) return <Image className="w-5 h-5 text-blue-500" />;
                              return <File className="w-5 h-5 text-slate-500" />;
                            };
                            const formatSize = (bytes) => {
                              if (bytes < 1024) return `${bytes} B`;
                              if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
                              return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
                            };
                            return (
                              <a
                                key={att.id}
                                href={`${API}/attachment/${currentEmail.id}/${selectedMessage.id}/${att.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 rounded-xl border border-slate-200 transition-colors group"
                              >
                                <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center border border-slate-200">
                                  {getFileIcon(att.content_type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-slate-700 truncate">{att.filename}</p>
                                  <p className="text-xs text-slate-400">{formatSize(att.size)}</p>
                                </div>
                                <Download className="w-5 h-5 text-slate-400 group-hover:text-indigo-600" strokeWidth={1.5} />
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    <div className="prose prose-slate max-w-none">
                      {selectedMessage.body.includes('<') ? (
                        <div dangerouslySetInnerHTML={{ __html: selectedMessage.body }} />
                      ) : (
                        <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{selectedMessage.body}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="shadow-soft border-slate-200 animate-fade-in-up stagger-2" data-testid="inbox-card">
                  <CardHeader className="border-b border-slate-100">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-medium text-slate-500 uppercase tracking-wider flex items-center gap-2">
                        <Inbox className="w-4 h-4" strokeWidth={1.5} />
                        Inbox
                        {unreadCount > 0 && <Badge className="bg-indigo-600 text-white rounded-full px-2 py-0.5 text-xs">{unreadCount}</Badge>}
                      </CardTitle>
                      <Button variant="ghost" onClick={refreshInbox} disabled={refreshing} className="rounded-full hover:bg-slate-100" data-testid="refresh-inbox-btn">
                        <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} strokeWidth={1.5} />
                        Refresh
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    {inbox.length === 0 ? (
                      <div className="empty-state py-16">
                        <div className="w-20 h-20 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                          <MailOpen className="w-10 h-10 text-slate-300" strokeWidth={1.5} />
                        </div>
                        <h3 className="font-heading text-lg font-semibold text-slate-700 mb-2">Inbox Kosong</h3>
                        <p className="text-slate-500 text-sm max-w-xs">Belum ada email masuk. Gunakan alamat email di atas untuk menerima pesan.</p>
                      </div>
                    ) : (
                      <ScrollArea className="h-[500px]">
                        <div className="divide-y divide-slate-100">
                          {inbox.map((message) => (
                            <div key={message.id} className={`p-4 cursor-pointer email-list-item ${!message.is_read ? 'bg-indigo-50/50' : ''}`} onClick={() => viewMessage(message)}>
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    {!message.is_read && <div className="w-2 h-2 rounded-full bg-indigo-600" />}
                                    <p className={`text-sm truncate ${!message.is_read ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>{message.sender}</p>
                                    {message.attachments && message.attachments.length > 0 && (
                                      <span className="flex items-center gap-1 text-xs text-slate-400">
                                        <Paperclip className="w-3 h-3" strokeWidth={1.5} />
                                        {message.attachments.length}
                                      </span>
                                    )}
                                  </div>
                                  <p className={`text-sm truncate mb-1 ${!message.is_read ? 'font-medium text-slate-800' : 'text-slate-600'}`}>{message.subject}</p>
                                  <p className="text-xs text-slate-400 truncate">{message.body.replace(/<[^>]*>/g, '').substring(0, 100)}...</p>
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                  <span className="text-xs text-slate-400 whitespace-nowrap">{formatDate(message.received_at)}</span>
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-500 hover:bg-red-50" onClick={(e) => e.stopPropagation()}>
                                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Hapus Pesan?</AlertDialogTitle>
                                        <AlertDialogDescription>Pesan ini akan dihapus permanen.</AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel className="rounded-full">Batal</AlertDialogCancel>
                                        <AlertDialogAction className="rounded-full bg-red-500 hover:bg-red-600" onClick={() => deleteMessage(message.id)}>Hapus</AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </main>

      <footer className="border-t border-slate-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-sm text-slate-400">TempMail - Email sementara yang aman</p>
        </div>
      </footer>
    </div>
  );
}

// App with Routes
function App() {
  return (
    <Routes>
      <Route path="/" element={<MainApp />} />
      <Route path="/:emailAddress" element={<EmailView />} />
    </Routes>
  );
}

export default App;
